import { getCommonDate } from 'app/pages/loanApplication/LoanApplication.utils';
import { CustomAdjustmentType } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LockInfo/LockInfo.constants';
import {
  getLockExtensionName,
  removeSecondsFromRateSheetTitle,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LockInfo/LockInfo.utils';
import {
  enhancePropertyExpensesPendingChanges,
  mapProposedPropertyExpensesFields,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/PropertyExpenses/PropertyExpenses.utils';
import dayjs from 'dayjs';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { COMMON_DATE_FORMAT } from 'constants/date';
import {
  fixedTwoFractionDigitsExceptIntegerCurrencyOptions,
  HelocPurposeType,
  PurchaseProcessType,
} from 'constants/loanApplication';
import { LoanChangesHistoryEntityType } from 'constants/loanChanges';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import { PROGRAM_LOAN_TYPES } from 'constants/programType';
import {
  getConditionalArray,
  getConditionalObject,
  getConditionalValue,
  getSplittedAddress,
} from 'utils/data';
import { replaceTimezonePostfix } from 'utils/dayjs';
import {
  checkIsAdditionalLoanFlow,
  checkIsChattelLoanPurpose,
  checkIsChattelLoanType,
  checkIsPurchaseFlow,
  checkIsRefinanceFlow,
  checkIsUnsecuredLoanType,
  getPersonFullName,
} from 'utils/loanApplication';
import { formatLockExpirationDate } from 'utils/lockExpiration';
import { formatNumberToCurrencyString, formatNumberToFixed3Decimals } from 'utils/number';
import { checkIsPendingChangeHasDifference } from 'utils/pendingChanges';

import { selectCustomAdjustments } from 'store/programs/selectors';

import { selectDealFromApplication } from './deal.selectors';
import { selectLoanApplication } from './loanApplication.selectors';
import {
  selectCommonInfoLoanApplication,
  selectIsLoanOptimalBlueOrSifterEngineType,
} from './loanApplicationCommonInfo.selectors';
import {
  selectEntitiesChangedPropsByIds,
  selectEntityLoanChangesByEntityTypeName,
} from './loanChangesHistory.selectors';

const selectPropertyAndLoan = createSelector(
  selectLoanApplication,
  ({ propertyAndLoan }) => propertyAndLoan,
);

const selectPropertyAndLoanSummary = createSelector(
  selectPropertyAndLoan,
  ({ summary }) => summary ?? {},
);

export const selectIsPropertyAndLoanSummaryEmpty = createSelector(
  selectPropertyAndLoan,
  ({ summary }) => _.isEmpty(summary),
);

const selectLoanAndLockEditData = createSelector(
  selectPropertyAndLoan,
  ({ loanAndLockEditData }) => loanAndLockEditData ?? {},
);

const selectLockInfoEditData = createSelector(selectLoanAndLockEditData, ({ lock }) => lock ?? {});

const selectProfitAndLoss = createSelector(
  selectLoanAndLockEditData,
  ({ profitAndLoss }) => profitAndLoss ?? {},
);

export const selectTotalLoanAmount = createSelector(
  selectLoanAndLockEditData,
  data => data?.loan?.details?.totalLoanAmount ?? 0,
);

export const selectCreditReportStatus = createSelector(
  selectLoanAndLockEditData,
  data => data?.loan?.additionalInformation?.creditReportStatus,
);

export const selectLoanProgramRate = createSelector(
  selectLoanAndLockEditData,
  data => data?.loan?.program?.rate ?? null,
);

export const selectSellerSideAdjustments = createSelector(
  selectProfitAndLoss,
  ({ sellerSideAdjustments }) => sellerSideAdjustments ?? [],
);

export const selectLockInfoSummaryData = createSelector(selectLockInfoEditData, lock => ({
  ..._.omit(lock, ['adjustments', 'markups']),
  lockExpirationDate: formatLockExpirationDate(lock.lockExpirationDate),
  lockDate: getConditionalValue(lock.lockDate, dayjs(lock.lockDate).format(COMMON_DATE_FORMAT)),
  buyPrice: getConditionalValue(lock.buyPrice, formatNumberToFixed3Decimals(lock.buyPrice)),
  finalPrice: getConditionalValue(lock.finalPrice, formatNumberToFixed3Decimals(lock.finalPrice)),
  rateSheetTitle: getConditionalValue(
    lock.rateSheetTitle,
    removeSecondsFromRateSheetTitle(lock.rateSheetTitle),
  ),
}));

const selectLockInfoAdjustmentsEditData = createSelector(
  selectLockInfoEditData,
  ({ adjustments }) => adjustments ?? {},
);

const selectLoanAndLockEditCustomAdjustments = createSelector(
  selectLockInfoAdjustmentsEditData,
  ({ customAdjustments }) => customAdjustments,
);

const selectCustomAdjustmentsWithNames = createSelector(
  [selectLoanAndLockEditCustomAdjustments, selectCustomAdjustments],
  (customAdjustmentsWithAmount, customAdjustmentsWithNames) => {
    if (!customAdjustmentsWithAmount || !customAdjustmentsWithNames) {
      return null;
    }

    return customAdjustmentsWithAmount.map(
      ({ customAdjustmentTypeId, loanCustomAdjustmentId, amount, name }) => ({
        name: name ?? customAdjustmentsWithNames[customAdjustmentTypeId]?.name,
        id: loanCustomAdjustmentId ?? customAdjustmentTypeId,
        amount,
        type: CustomAdjustmentType.CUSTOM_ADJUSTMENT,
        b: customAdjustmentsWithNames[customAdjustmentTypeId]?.name,
      }),
    );
  },
);

export const selectFormattedLoanAndLockEditAdjustments = createSelector(
  [
    selectLockInfoAdjustmentsEditData,
    selectCustomAdjustmentsWithNames,
    selectDealFromApplication,
    selectIsLoanOptimalBlueOrSifterEngineType,
  ],
  (adjustments, formattedCustomAdjustments, { loanId }, isOBEngineType) => {
    if (_.isNil(adjustments)) {
      return null;
    }

    if (_.isEmpty(adjustments)) {
      return [];
    }

    const { lockExtensions, floatDowns, autoAdjustments } = adjustments;

    const formattedLockExtensions = lockExtensions.map(item => ({
      ...item,
      id: item.lockExtensionId,
      amount: item.price,
      name: getLockExtensionName({ item, isExternal: isOBEngineType }),
      type: CustomAdjustmentType.EXTENSION,
    }));

    const formattedFloatDown = floatDowns.map(item => ({
      ...item,
      name: 'Float down',
      amount: item.cost,
      id: `${loanId}_${replaceTimezonePostfix(item.createdDate)}`,
      type: CustomAdjustmentType.FLOAT_DOWN,
    }));

    const formattedAutoAdjustments = autoAdjustments.map(item => ({
      ...item,
      id: item.criteriaId,
      type: CustomAdjustmentType.AUTO_ADJUSTMENTS,
      originalType: item.type,
    }));

    const flatAdjustments = [
      ...formattedAutoAdjustments,
      ...formattedCustomAdjustments,
      ...formattedLockExtensions,
      ...formattedFloatDown,
    ];

    return flatAdjustments;
  },
);

export const selectInvalidTabs = createSelector(
  selectPropertyAndLoan,
  ({ invalidTabs }) => invalidTabs,
);

export const selectLoanAppraisalSurcharges = createSelector(
  selectPropertyAndLoan,
  ({ loanAppraisalSurcharges }) => loanAppraisalSurcharges ?? [],
);

const selectPropertyAndLoanEditData = createSelector(
  selectPropertyAndLoan,
  ({ propertyAndLoanEditData }) => {
    if (!propertyAndLoanEditData) {
      return {};
    }

    const { propertyExpenses, ...restEditData } = propertyAndLoanEditData;

    const fixedPropertyExpenses = {
      ...propertyExpenses,
      proposedPropertyExpenses: mapProposedPropertyExpensesFields(
        propertyExpenses?.proposedPropertyExpenses,
      ),
    };

    return { propertyExpenses: fixedPropertyExpenses, ...restEditData };
  },
);

export const selectPropertyAndLoanEditDataHeaderInfo = createSelector(
  selectPropertyAndLoanEditData,
  ({ headerInfo }) => headerInfo ?? {},
);

const selectPropertyAndLoanEditDataPurchaseProcessType = createSelector(
  selectPropertyAndLoanEditData,
  ({ purchaseProcessType }) => purchaseProcessType,
);

export const selectIsTbdPropertyAndLoanEditData = createSelector(
  selectPropertyAndLoanEditDataPurchaseProcessType,
  purchaseProcessType =>
    [
      PurchaseProcessType.SearchingOrShoppingAround,
      PurchaseProcessType.MakingOfferOrSigningPurchaseContract,
    ].some(i => purchaseProcessType === i),
);

export const selectPropertyAndLoanSummarySubjectPropertyData = createSelector(
  selectPropertyAndLoanSummary,
  selectCommonInfoLoanApplication,
  ({ subjectProperty, lockLoan }, { loanType } = {}) => {
    if (!subjectProperty) {
      return {};
    }

    const [propertyAddressLine1, propertyAddressLine2] = getSplittedAddress(
      subjectProperty.propertyAddress,
    );

    return {
      ...subjectProperty,
      propertyAddressLine1,
      propertyAddressLine2,
      loanTypeValue: loanType ?? lockLoan?.program?.loanType ?? null,
    };
  },
);

const selectPropertyAndLoanSummarySecondaryFinancingData = createSelector(
  selectPropertyAndLoanSummary,
  ({ secondaryFinancing }) => secondaryFinancing ?? [],
);

export const selectSecondaryFinancingAdditionalMortgagesData = createSelector(
  selectPropertyAndLoanSummarySecondaryFinancingData,
  ({ additionalMortgages }) => additionalMortgages ?? [],
);

export const selectSecondaryFinancingLiabilitiesData = createSelector(
  [selectPropertyAndLoanSummarySecondaryFinancingData, state => state],
  ({ liabilities = [] }, state) =>
    liabilities.map(liability => {
      const borrowersPendingChanges = selectEntitiesChangedPropsByIds(liability.owners)(state);

      return {
        ...liability,
        ownersInfo: liability.owners.map((id, index) => ({
          id,
          fullName: getPersonFullName({
            ...liability.ownersFullNames[index],
            isWithMiddleName: true,
            isWithSuffix: true,
          }),
          modifiedFields: Object.keys(borrowersPendingChanges[id] || {}).map(key =>
            _.upperFirst(key),
          ),
        })),
      };
    }),
);

const selectLockAndLoanInfo = createSelector(
  selectPropertyAndLoanSummary,
  ({ lockLoan }) => lockLoan ?? {},
);

// Raw inputs the server's MaturityDateCalculator uses (first payment date + program term),
// so the Closing drawer can project the read-only Maturity Date live for a loan
// that has no persisted maturity yet. Loaded with the property-and-loan summary.
export const selectMaturityCalcInputs = createSelector(
  selectPropertyAndLoanSummary,
  ({ lockLoan } = {}) => ({
    programLoanType: lockLoan?.program?.loanType ?? null,
    loanTerm: lockLoan?.program?.loanTerm ?? null,
    initialDrawPeriodMonths: lockLoan?.program?.initialDrawPeriod?.period ?? null,
    repaymentPeriodMonths: lockLoan?.program?.repaymentPeriod?.period ?? null,
  }),
);

const formatTermMonths = (months, monthsPerYear) => {
  if (!months) {
    return months;
  }

  const years = months / monthsPerYear;

  return Number.isInteger(years) ? `${months} Months (${years} Yr)` : `${months} Months`;
};

// Display label from the full catalog: programLoanTypeOptions omits Chattel (it is not selectable
// when creating a program), which left chattel loans showing an empty/"N/A" Loan Type.
const getProgramLoanTypeName = loanType =>
  PROGRAM_LOAN_TYPES.find(({ value }) => value === loanType)?.name;

const formatIoPeriods = (formatPeriod, interestOnlyPeriod, amortizationTerm) => {
  const io = formatPeriod(interestOnlyPeriod);
  const repay = formatPeriod(
    interestOnlyPeriod && amortizationTerm ? amortizationTerm - interestOnlyPeriod : null,
  );

  if (io && repay) {
    return `${io} I/O / ${repay} Repay`;
  }

  if (io) {
    return `${io} I/O`;
  }

  if (repay) {
    return `${repay} Repay`;
  }

  return null;
};

export const selectLockAndLoanInfoSummary = createSelector(
  selectLockAndLoanInfo,
  selectLoanAndLockEditData,
  (lockLoan, editData) => {
    const { purpose, program, interestRate, mortgageInsurance } = lockLoan;
    const loanProgram = editData?.loanProgram;

    const toCurrency = value =>
      _.isNil(value)
        ? null
        : formatNumberToCurrencyString(value, fixedTwoFractionDigitsExceptIntegerCurrencyOptions);

    const hasMi = !!(
      mortgageInsurance?.pmi ||
      mortgageInsurance?.miCoveragePercent ||
      mortgageInsurance?.miTerm
    );

    const isInterestOnly = program?.interestOnly ?? loanProgram?.loanCharacteristics?.interestOnly;
    const interestOnlyPeriod = program?.interestOnlyPeriod ?? loanProgram?.interestOnlyPeriod;
    const amortizationTerm = program?.amortizationTerm;
    const monthsPerYear = 12;

    const formatPeriod = months => {
      if (!months) {
        return null;
      }

      const years = months / monthsPerYear;

      return Number.isInteger(years) ? `${years} yr` : `${months} mo`;
    };

    return {
      purpose,
      program: {
        ...program,
        escrow: program?.escrow ? 'Yes' : 'No',
        loanType: getProgramLoanTypeName(program?.loanType),
        loanTerm: formatTermMonths(program?.loanTerm, monthsPerYear),
        amortizationTerm: formatTermMonths(amortizationTerm, monthsPerYear),
        investorName: program?.investorName || null,
        ...(isInterestOnly
          ? {
              interestOnly: true,
              ioPeriods: formatIoPeriods(formatPeriod, interestOnlyPeriod, amortizationTerm),
            }
          : {}),
      },
      interestRate: {
        ...(interestRate?.lockDate ? interestRate : _.omit(interestRate, ['lockTerm'])),
        lockDate: getCommonDate(interestRate?.lockDate),
        lockExpiration: getCommonDate(interestRate?.lockExpiration),
      },
      mortgageInsurance: {
        hasMi,
        ...getConditionalObject(hasMi, () => ({
          miCoveragePercent: !_.isNil(mortgageInsurance?.miCoveragePercent)
            ? `${mortgageInsurance.miCoveragePercent}%`
            : null,
          pmiPercent: !_.isNil(mortgageInsurance?.pmiPercent)
            ? `${mortgageInsurance.pmiPercent}%`
            : null,
          pmi: toCurrency(mortgageInsurance?.pmi),
          miTerm: mortgageInsurance?.miTerm,
          miAndFundingFeeFinancedAmount: toCurrency(
            mortgageInsurance?.miAndFundingFeeFinancedAmount,
          ),
          miAndFundingFeesAmount: toCurrency(mortgageInsurance?.miAndFundingFeesAmount),
        })),
      },
    };
  },
);

const PERCENT_DIVISOR = 100;
const MONTHS_PER_YEAR = 12;

const calculateInterestOnlyPayment = (totalLoanAmount, annualRate) => {
  if (totalLoanAmount == null || annualRate == null) {
    return null;
  }

  const monthlyRate = annualRate / PERCENT_DIVISOR / MONTHS_PER_YEAR;

  return Math.round(totalLoanAmount * monthlyRate * PERCENT_DIVISOR) / PERCENT_DIVISOR;
};

export const selectLoanAndLockInfoMetrics = createSelector(
  selectLockAndLoanInfo,
  selectPropertyAndLoanSummary,
  selectLoanAndLockEditData,
  selectCommonInfoLoanApplication,
  (lockLoan, summary, editData, { loanType }) => {
    const { program, chartLegend, interestRate } = lockLoan;
    const { subjectProperty } = summary;
    const loanProgram = editData?.loanProgram;

    const isInterestOnly =
      program?.interestOnly ??
      loanProgram?.loanCharacteristics?.interestOnly ??
      subjectProperty?.interestOnly;

    const interestOnlyPayment = isInterestOnly
      ? (program?.interestOnlyPayment ??
        subjectProperty?.interestOnlyPayment ??
        calculateInterestOnlyPayment(chartLegend?.totalLoanAmount, interestRate?.interestRate))
      : null;

    const interestOnlyPeriod = program?.interestOnlyPeriod ?? loanProgram?.interestOnlyPeriod;
    const amortizationTerm = program?.amortizationTerm;
    const hasRepaymentPeriod =
      isInterestOnly && interestOnlyPeriod && amortizationTerm
        ? interestOnlyPeriod < amortizationTerm
        : false;

    const metricsResult = {
      totalLoanAmount: chartLegend?.totalLoanAmount,
      helocDrawAmount: chartLegend?.helocDrawAmount,
      loanTypeValue: loanType ?? program?.loanType,
      ltv: chartLegend?.ltv,
      cltv: chartLegend?.cltv,
      hltv: chartLegend?.hltv,
      loanType: getProgramLoanTypeName(program?.loanType),
      principalAndInterest:
        editData?.lock?.principalAndInterest ?? subjectProperty?.principalAndInterest,
      monthlyPayment: subjectProperty?.monthlyPayment,
      drawBasedMonthlyPayment: subjectProperty?.drawBasedMonthlyPayment,
      qualifyingMonthlyPayment: subjectProperty?.qualifyingMonthlyPayment ?? null,
      piti: subjectProperty?.piti ?? null,
      interestOnly: isInterestOnly,
      interestOnlyPayment,
      hasRepaymentPeriod,
      chartLegendModifiedFields: chartLegend?.modifiedFields,
      programModifiedFields: program?.modifiedFields,
      subjectPropertyModifiedFields: subjectProperty?.modifiedFields,
      chartLegendModifiedFieldDetails: chartLegend?.modifiedFieldDetails,
      programModifiedFieldDetails: program?.modifiedFieldDetails,
      subjectPropertyModifiedFieldDetails: subjectProperty?.modifiedFieldDetails,
    };

    return metricsResult;
  },
);

export const selectKeyDates = createSelector(
  selectLockAndLoanInfo,
  ({ keyDates }) => keyDates ?? {},
);

const selectLockDateFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  ({ interestRate }) => interestRate?.lockDate ?? null,
);

export const selectIsLoanLockedFromPropertyAndLoanSummary = createSelector(
  selectLockDateFromPropertyAndLoanSummary,
  lockDate => !!lockDate,
);

export const selectChartLegend = createSelector(selectLockAndLoanInfo, lockLoan => ({
  ...lockLoan?.chartLegend,
  loanPurpose: lockLoan?.purpose?.loanPurpose,
}));

export const selectIsHelocPurposePurchaseFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => lockLoan.purpose?.helocPurpose === HelocPurposeType.HOME_PURCHASE,
);

export const selectIsHelocPurposeRefinanceFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => lockLoan.purpose?.helocPurpose === HelocPurposeType.REFINANCE,
);

export const selectIsLoanPurposeRefinanceFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => checkIsRefinanceFlow(lockLoan.purpose?.loanPurpose),
);

export const selectIsLoanPurposePurchaseFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => checkIsPurchaseFlow(lockLoan.purpose?.loanPurpose),
);

export const selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => checkIsAdditionalLoanFlow(lockLoan.purpose?.loanPurpose),
);

export const selectLoanPurposeFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => lockLoan.purpose?.loanPurpose,
);

export const selectLoanTypeFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => lockLoan.program?.loanType,
);

export const selectIsUnsecuredFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan => checkIsUnsecuredLoanType(lockLoan.program?.loanType),
);

// A loan is chattel either because a chattel PROGRAM is selected (POS flow) or because its
// LOS loan purpose is Chattel (LEN-25 — set in the LOS without POS, so no program exists yet).
// Gating only on program.loanType left the specialized chattel fields hidden for LOS chattel
// loans, which have no program (LEN-38).
export const selectIsChattelLoanFromPropertyAndLoanSummary = createSelector(
  selectLockAndLoanInfo,
  lockLoan =>
    checkIsChattelLoanType(lockLoan.program?.loanType) ||
    checkIsChattelLoanPurpose(lockLoan.purpose?.loanPurpose),
);

// `isConstruction` is a loan-level marker on the GET /loan-info (LoanSummary) response, which lands in
// the loan-and-lock edit-data slice — the same source the sibling loan-info-derived selectors read. The
// subject-property summary view does NOT carry the field, so derive it from the loan-info-fed state.
export const selectIsConstructionFromPropertyAndLoanSummary = createSelector(
  selectLoanAndLockEditData,
  loanAndLockEditData => loanAndLockEditData.isConstruction ?? false,
);

const buildAppraisalSurchargesPendingChange = ({
  surchargeChangesByRow,
  loanAppraisalSurcharges,
}) => {
  const addedCatalogIds = [];
  const deletedCatalogIds = [];

  Object.values(surchargeChangesByRow).forEach(changes => {
    const surchargeChanges = changes.filter(
      ({ entityTypeName }) =>
        entityTypeName === LoanChangesHistoryEntityType.LOAN_APPRAISAL_SURCHARGE,
    );
    const rowActionType = surchargeChanges[0]?.actionType;
    const surchargeIdChange = surchargeChanges.find(
      ({ propertyName }) => propertyName === 'appraisalSurchargeId',
    );

    if (!surchargeIdChange) {
      return;
    }

    if (rowActionType === HistoryPendingChangesActionType.ADDED) {
      addedCatalogIds.push(surchargeIdChange.newValue);
    }

    if (rowActionType === HistoryPendingChangesActionType.DELETED) {
      deletedCatalogIds.push(surchargeIdChange.oldValue);
    }
  });

  if (addedCatalogIds.length === 0 && deletedCatalogIds.length === 0) {
    return null;
  }

  const newValue = loanAppraisalSurcharges.map(({ appraisalSurchargeId }) => appraisalSurchargeId);
  const initialValue = [
    ...newValue.filter(id => !addedCatalogIds.includes(id)),
    ...deletedCatalogIds,
  ];

  return {
    newValue,
    initialValue,
    actionType: HistoryPendingChangesActionType.MODIFIED,
  };
};

export const selectPropertyPendingChanges = createSelector(
  [selectPropertyAndLoanEditData, selectPropertyAndLoanSummarySubjectPropertyData, state => state],
  (editData, subjectPropertyData, state) => {
    const { loanId, applicationId } = selectDealFromApplication(state);
    const { propertyId: subjectPropertyId, propertyAddressId: addressId } = subjectPropertyData;

    const agentsIds =
      editData.underwritingAgentsAndSellersData?.underwritingAgents.map(
        ({ loanUnderwritingAgentId }) => loanUnderwritingAgentId,
      ) || [];

    const sellersIds =
      editData.underwritingAgentsAndSellersData?.underwritingSellers?.map(
        ({ loanUnderwritingSellerId }) => loanUnderwritingSellerId,
      ) || [];

    const propertyTaxesIds =
      editData.propertyExpenses?.proposedPropertyExpenses?.propertyTaxes?.map(
        ({ applicationImpoundId }) => applicationImpoundId,
      ) || [];

    const trustId = editData.trustInfo?.id;

    const supplementalInsurancesIds =
      editData.propertyExpenses?.proposedPropertyExpenses?.supplementalInsurances?.map(
        ({ applicationImpoundId }) => applicationImpoundId,
      ) || [];

    const entitiesIds = [
      loanId,
      applicationId,
      subjectPropertyId,
      addressId,
      ...getConditionalArray(!!trustId, [trustId]),
    ];
    const unitsIds = _.sortBy(editData.appraisal?.uniformCollateralDataPortal?.units, [
      'orderNumber',
    ]).map(({ propertyUnitId }) => propertyUnitId);

    const propertyPendingChanges = selectEntitiesChangedPropsByIds([...entitiesIds, ...unitsIds])(
      state,
    );

    const agentsAndSellersPendingChanges = selectEntitiesChangedPropsByIds([
      ...agentsIds,
      ...sellersIds,
    ])(state);

    const propertyTaxesPendingChanges = selectEntitiesChangedPropsByIds(propertyTaxesIds)(state);

    const supplementalInsurancesPendingChanges =
      selectEntitiesChangedPropsByIds(supplementalInsurancesIds)(state);

    const surchargeChangesByRow = selectEntityLoanChangesByEntityTypeName(
      LoanChangesHistoryEntityType.LOAN_APPRAISAL_SURCHARGE,
    )(state);
    const loanAppraisalSurcharges = selectLoanAppraisalSurcharges(state);
    const appraisalSurchargesPendingChange = buildAppraisalSurchargesPendingChange({
      surchargeChangesByRow,
      loanAppraisalSurcharges,
    });

    const orderContactChangesByRow = selectEntityLoanChangesByEntityTypeName(
      LoanChangesHistoryEntityType.LOAN_APPRAISAL_ORDER_CONTACT,
    )(state);
    const appraisalOrderContactsPendingChanges = selectEntitiesChangedPropsByIds(
      Object.keys(orderContactChangesByRow),
    )(state);

    return {
      ...entitiesIds.reduce((acc, id) => {
        if (id === trustId && !_.isEmpty(propertyPendingChanges[id])) {
          const { type, name, ...otherTrustPendingChanges } = propertyPendingChanges[id];

          return {
            ...acc,
            ...otherTrustPendingChanges,
            trustType: type,
            trustName: name,
          };
        }

        return {
          ...acc,
          ...propertyPendingChanges[id],
        };
      }, {}),
      agentsAndSellersPendingChanges,
      propertyTaxesPendingChanges: enhancePropertyExpensesPendingChanges(
        propertyTaxesPendingChanges,
      ),
      supplementalInsurancesPendingChanges: enhancePropertyExpensesPendingChanges(
        supplementalInsurancesPendingChanges,
        false,
      ),
      units: unitsIds.map(id => propertyPendingChanges[id] ?? {}),
      ...getConditionalObject(!!appraisalSurchargesPendingChange, {
        appraisalSurcharges: appraisalSurchargesPendingChange,
      }),
      ...getConditionalObject(!_.isEmpty(appraisalOrderContactsPendingChanges), {
        appraisalOrderContacts: appraisalOrderContactsPendingChanges,
      }),
    };
  },
);

export const selectLoanAndLockPendingChanges = createSelector(
  [selectLoanAndLockEditData, state => state],
  (editData, state) => {
    const { loanId, applicationId } = selectDealFromApplication(state);

    const additionalMortgagesIds = editData.additionalMortgages?.map(({ id }) => id) ?? [];

    const loanTagsIds =
      editData.loanTagAssignments?.map(({ tagId }) => `${tagId}${applicationId}`) ?? [];

    const loanRolesIds =
      editData.loanRoleAssignments?.map(({ loanRoleId }) => `${loanRoleId}${applicationId}`) ?? [];

    const { customAdjustments, autoAdjustments, floatDowns, lockExtensions } =
      editData.lock?.adjustments || {};

    const obCustomFields = editData.loan?.optimalBlueCustomFieldsData.customFields || [];

    const customAdjustmentsIds =
      customAdjustments?.map(({ loanCustomAdjustmentId }) => loanCustomAdjustmentId) ?? [];

    const autoAdjustmentsIds = autoAdjustments?.map(({ criteriaId }) => criteriaId) ?? [];

    const floatDownsIds =
      floatDowns?.map(({ createdDate }) => `${loanId}_${replaceTimezonePostfix(createdDate)}`) ??
      [];

    const lockExtensionsIds = lockExtensions?.map(({ lockExtensionId }) => lockExtensionId) ?? [];
    const entitiesIds = [loanId, applicationId];
    const creditsMortgagesIds = editData.credits?.map(({ id }) => id) ?? [];

    const { escrowSettings } = editData.loan?.details ?? {};
    const insuranceEscrowSettingsIds =
      escrowSettings?.insuranceEscrowSettings?.insuranceImpoundsEscrowed?.map(
        ({ applicationInsuranceId }) => applicationInsuranceId,
      ) ?? [];

    const loanPendingChanges = selectEntitiesChangedPropsByIds(
      [
        ...entitiesIds,
        ...additionalMortgagesIds,
        ...customAdjustmentsIds,
        ...autoAdjustmentsIds,
        ...floatDownsIds,
        ...lockExtensionsIds,
        ...creditsMortgagesIds,
        ...loanTagsIds,
        ...loanRolesIds,
        ...insuranceEscrowSettingsIds,
      ],
      false,
    )(state);
    const getAdjustmentsIdByLoanId = id => `${id}_${loanId}`;

    const autoAdjustmentsPendingChanges = autoAdjustmentsIds.reduce((acc, id) => {
      const change = loanPendingChanges[getAdjustmentsIdByLoanId(id)];

      if (!change) {
        return acc;
      }

      acc[id] = {
        ...(change.amount && { amount: change.amount }),
        name: change.criteriaId,
      };

      return acc;
    }, {});

    const customAdjustmentsPendingChanges = customAdjustmentsIds.reduce((acc, id) => {
      const change = loanPendingChanges[id];

      if (!change) {
        return acc;
      }

      acc[id] = { amount: change.amount, name: change.loanCustomAdjustmentId };

      return acc;
    }, {});

    const floatDownsPendingChanges = floatDowns?.reduce((acc, { createdDate }) => {
      const id = `${loanId}_${replaceTimezonePostfix(createdDate)}`;
      const change = loanPendingChanges[id];

      if (!change) {
        return acc;
      }

      acc[id] = { amount: change.cost, name: change.llpaTypeId };

      return acc;
    }, {});

    const lockExtensionsPendingChanges = lockExtensionsIds.reduce((acc, id) => {
      const change = loanPendingChanges[id];

      if (!change) {
        return acc;
      }

      acc[id] = { amount: change.price, name: change.lockExtensionType };

      return acc;
    }, {});

    const loanTagsPendingChanges = loanTagsIds.reduce((acc, id) => {
      const loanTagPendingChanges = loanPendingChanges[id];

      if (loanTagPendingChanges) {
        const loanTagId = id.replace(applicationId, '');

        acc[loanTagId] = { ...loanTagPendingChanges, isSeparateChange: true };
      }

      return acc;
    }, {});

    const loanRolesPendingChanges = loanRolesIds.reduce((acc, id) => {
      const loanRolePendingChanges = _.find(loanPendingChanges, (_, key) => key.includes(id));

      if (loanRolePendingChanges) {
        const loanRoleId = id.replace(applicationId, '');

        acc[loanRoleId] = loanRolePendingChanges;
      }

      return acc;
    }, {});

    const insuranceEscrowSettingsPendingChanges = insuranceEscrowSettingsIds.reduce((acc, id) => {
      const change = loanPendingChanges[id];

      if (!change) {
        return acc;
      }

      acc[id] = { ...change, isSeparateChange: true };

      return acc;
    }, {});

    const pendingChangesValues = Object.values(
      _.omit(loanPendingChanges, [
        ...additionalMortgagesIds,
        ...loanTagsIds,
        ...creditsMortgagesIds,
        ...loanRolesIds,
        ...insuranceEscrowSettingsIds,
      ]) || [],
    ).reduce(
      (acc, changes) => {
        return {
          ...acc,
          ...changes,
          programIdentifierId: changes.programIdentifierId
            ? [...(acc.programIdentifierId || []), changes.programIdentifierId]
            : acc.programIdentifierId,
          investorFeatureCodeId: changes.investorFeatureCodeId
            ? [...(acc.investorFeatureCodeId || []), changes.investorFeatureCodeId]
            : acc.investorFeatureCodeId,
        };
      },
      {
        additionalMortgages: additionalMortgagesIds.map(id => loanPendingChanges[id] ?? {}),
        adjustments: {
          ...customAdjustmentsPendingChanges,
          ...autoAdjustmentsPendingChanges,
          ...floatDownsPendingChanges,
          ...lockExtensionsPendingChanges,
        },
        credits: creditsMortgagesIds.reduce((acc, id) => {
          const {
            purchaseCreditSourceType,
            otherCreditSourceTypeOtherDescription,
            creditTypeOtherDescription,
            ...otherChanges
          } = loanPendingChanges[id] || {};
          acc[id] = {
            ...otherChanges,
            ...getConditionalObject(!!purchaseCreditSourceType, {
              sourceType: purchaseCreditSourceType,
            }),
            ...getConditionalObject(!!otherCreditSourceTypeOtherDescription, {
              otherDescription: otherCreditSourceTypeOtherDescription,
            }),
            ...getConditionalObject(!!creditTypeOtherDescription, {
              description: creditTypeOtherDescription,
            }),
          };

          return acc;
        }, {}),
        obCustomFields: obCustomFields.reduce((acc, i) => {
          acc[`${i.columnName}${i.name}${i.submissionForm}`] = {
            ...loanPendingChanges[`${loanId}_${i.submissionForm}_${i.name}`]?.fieldValue,
            isSeparateChange: true,
          };

          return acc;
        }, {}),
        loanTags: loanTagsPendingChanges,
        loanRoles: loanRolesPendingChanges,
        insuranceImpoundsEscrowed: insuranceEscrowSettingsPendingChanges,
      },
    );

    return _.omitBy(
      pendingChangesValues,
      i => i?.newValue !== undefined && !checkIsPendingChangeHasDifference(i),
    );
  },
);

/**
 * Normalizes propertyAndLoanEditData (raw API response) into the shape
 * expected by Universal Search's buildFieldsIndex for `subjectProperty`.
 *
 * The edit data API returns nested structures (e.g. occupancy: { type: 'PrimaryResidence' }),
 * while field configs expect flat values (e.g. occupancy: 'PrimaryResidence').
 */
export const selectSubjectPropertyForSearch = createSelector(
  selectPropertyAndLoan,
  ({ propertyAndLoanEditData }) => {
    if (!propertyAndLoanEditData) {
      return {};
    }

    const { property, propertyExpenses, appraisal, project, transactionDetails } =
      propertyAndLoanEditData;

    const propertyAndOccupancy = property?.propertyAndOccupancy;
    const propertyAddress = property?.propertyAddress;
    const address = propertyAddress?.address?.address;

    return {
      propertyAndOccupancy: {
        appraisedValue: propertyAndOccupancy?.appraisedValue,
        occupancy: propertyAndOccupancy?.occupancy?.type,
        isRelocationLoan: propertyAndOccupancy?.isRelocationLoan,
        propertyType: propertyAndOccupancy?.propertyType?.type,
        attachmentType: propertyAndOccupancy?.attachmentType?.type,
        status: propertyAndOccupancy?.status,
        numberOfUnits: propertyAndOccupancy?.propertyType?.numberOfUnits,
        propertyRightsEstateHeld: propertyAndOccupancy?.propertyRightsEstateHeld,
        leaseholdExpiration: propertyAndOccupancy?.leaseholdExpiration,
        propertyValue: propertyAndOccupancy?.purchasePrice,
        totalSalesConcessionAmount: propertyAndOccupancy?.totalSalesConcessionAmount,
        manufacturedHomeType: propertyAndOccupancy?.manufacturedHomeType,
        originalCost: propertyAndOccupancy?.originalCost,
        hasAffordableLegalRestrictions: propertyAndOccupancy?.hasAffordableLegalRestrictions,
        isConveyable: propertyAndOccupancy?.isConveyable,
        affordableHomeProviderEin: propertyAndOccupancy?.affordableHomeProviderEin,
        communityLandTrustEin: propertyAndOccupancy?.communityLandTrustEin,
      },
      propertyAddress: {
        street: address?.street,
        unit: address?.unit,
        city: address?.city,
        county: address?.county,
        state: address?.country?.state?.abbreviation,
        zip: address?.country?.zip,
        isDeedRestricted: propertyAddress?.isDeedRestricted,
        deedRestrictionType: propertyAddress?.deedRestrictionType,
        deedRestrictionMonths: propertyAddress?.deedRestrictionMonths,
        isNewConstruction: propertyAddress?.isNewConstruction,
        completionDate: propertyAddress?.completionDate,
        mixedUseProperty: propertyAddress?.mixedUseProperty,
        hasOweltyOfPartition: propertyAddress?.hasOweltyOfPartition,
      },
      proposedPropertyExpenses: propertyExpenses?.proposedPropertyExpenses,
      existingPropertyExpenses: propertyExpenses?.existingPropertyExpenses,
      appraisal,
      project,
      ...(transactionDetails
        ? {
            transactionDetail: transactionDetails?.transactionDetailInfo?.transactionDetailType,
            transactionDetailClosingType: transactionDetails?.transactionDetailInfo?.closingType,
            constructionOrImprovementCosts:
              transactionDetails?.transactionDetailInfo?.constructionOrImprovementCosts,
            energyRelatedImprovementsIndicator:
              transactionDetails?.energyRelatedImprovements?.energyRelatedImprovementsIndicator,
            energyImprovementAmount:
              transactionDetails?.energyRelatedImprovements?.energyImprovementAmount,
            amountOfEnergyEfficiencyMortgage:
              transactionDetails?.energyRelatedImprovements?.amountOfEnergyEfficiencyMortgage,
            listOfEnergyImprovements:
              transactionDetails?.energyRelatedImprovements?.listOfEnergyImprovements,
          }
        : {}),
    };
  },
);

/**
 * Normalizes loanAndLockEditData (raw API response) into the flat shape
 * expected by Universal Search's buildFieldsIndex for `loanApplicationData`.
 *
 * The edit data API nests fields under details/additionalInformation/program,
 * while field configs expect flat field names (e.g. baseLoanAmount, loanPurpose).
 */
export const selectNormalizedLoanDataForSearch = createSelector(
  selectLoanAndLockEditData,
  editData => {
    if (_.isEmpty(editData)) {
      return {};
    }

    const { details, additionalInformation, credits, program = {} } = editData;

    const escrowSettings = details?.escrowSettings;

    return {
      // program details
      programName: program.name,
      rate: program.rate,
      loanPeriod: program.loanPeriod,
      mortgageType: program.mortgageType,
      amortizationType: program.amortizationType,
      amortizationTerm: program.amortizationTerm,
      // loan details
      baseLoanAmount: details?.baseLoanAmount,
      totalLoanAmount: details?.totalLoanAmount,
      loanPurpose: details?.purposeType?.type,
      refinancePurpose: details?.purposeType?.refinancePurpose,
      cashOutAmount: details?.purposeType?.cashOutAmount,
      helocPurpose: details?.purposeType?.helocPurpose,
      tangibleNetBenefitReason: details?.purposeType?.tangibleNetBenefit?.reason,
      tangibleNetBenefitCurrentInterestRate:
        details?.purposeType?.tangibleNetBenefit?.currentInterestRate,
      tangibleNetBenefitCurrentAmortizationPeriodMonths:
        details?.purposeType?.tangibleNetBenefit?.currentAmortizationPeriodMonths,
      tangibleNetBenefitDescription: details?.purposeType?.tangibleNetBenefit?.description,
      additionalLoanSecondMortgageType: details?.purposeType?.additionalLoanSecondMortgageType,
      additionalLoanDrawAmount: details?.purposeType?.additionalLoanDrawAmount,
      lienPriority: details?.lienPriority,
      escrowSettings,
      vaTotalEntitlement: details?.vaTotalEntitlement,
      // additional loan information
      areaMedianIncome: additionalInformation?.areaMedianIncome,
      investorCodes: additionalInformation?.investorCodes,
      leadSource:
        additionalInformation?.leadSource?.type === 'Empty'
          ? null
          : additionalInformation?.leadSource?.leadSourceId,
      loanProgramIdentifiers: additionalInformation?.loanProgramIdentifiers,
      usdaEligible: additionalInformation?.usdaEligible,
      loanWorkflow: additionalInformation?.loanWorkflow,
      nationalHousingActSection: additionalInformation?.nationalHousingActSection,
      fha203kConsultantId: additionalInformation?.fha203kConsultantId,
      fhaBorrowerCertification: additionalInformation?.fhaBorrowerCertification,
      agencyCaseNumber: additionalInformation?.agencyCaseNumber,
      agencyCaseNumberIssueDate: additionalInformation?.agencyCaseNumberIssueDate,
      minimumResidualIncome: additionalInformation?.minimumResidualIncome,
      governmentRefinancePurpose: additionalInformation?.governmentRefinancePurpose,
      housingFinanceAgencyId: additionalInformation?.hfaInfo?.id,
      housingFinanceAgencyOtherDescription: additionalInformation?.hfaInfo?.otherDescription,
      documentVerificationAssetType: editData.documentVerificationTypes?.asset,
      documentVerificationEmploymentType: editData.documentVerificationTypes?.employment,
      documentVerificationIncomeType: editData.documentVerificationTypes?.income,
      // credits (normalized to flat shape)
      credits: credits?.map(({ id, creditType, amount }) => ({
        id,
        description: creditType?.description,
        purchaseCreditType: creditType?.purchaseCreditType,
        sourceType: creditType?.sourceType?.type,
        otherDescription: creditType?.sourceType?.description,
        creditType: creditType?.type,
        amount,
      })),
    };
  },
);
